// Search data — collection and settings.
//
// This surface used to carry eleven tabs of analysis. Those questions are now
// asked better on their own screens — Keyword Performance, Term Performance,
// Visibility, CTR Optimizer and Search Attribution each answer to one document
// and can be checked against it. Leaving copies here meant two places to change
// and two places to disagree.
//
// What stayed is everything the other five cannot do. They only read: nothing
// on them fetches a report from Amazon, and nothing on them tells us which
// brands are the seller's own or which rivals they want tracked. Without this
// page no new window would ever arrive and the competitor filters would have
// nothing to filter on — so it is not the old dashboard trimmed, it is the
// control room the rest depends on.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLink, FiLock } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './SearchTerms.module.css';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import CollectPanel from './components/CollectPanel';
import TermDetail from './components/TermDetail';
import { NotConnectedError, getSettings, saveSettings } from './api';

// Where each question moved, so a seller who came here looking for it is told
// rather than left to hunt.
const MOVED = [
  ['Top terms, conversion, gaps, cannibalization', 'Term Performance',
   '/dashboard/seller-intelligence/term-performance'],
  ['Ranking, slipping, opportunity, competitor wins, effectiveness', 'Keyword Performance',
   '/dashboard/seller-intelligence/keyword-performance'],
  ['Revenue from search, dependency, long tail', 'Search Attribution',
   '/dashboard/seller-intelligence/search-attribution'],
  ['Sessions, page views, visibility score', 'Visibility',
   '/dashboard/seller-intelligence/visibility'],
  ['What changed — terms that slipped or a rival took', 'Keyword Performance',
   '/dashboard/seller-intelligence/keyword-performance'],
  ['What changed — products leaning on one term or losing search share',
   'Search Attribution', '/dashboard/seller-intelligence/search-attribution'],
];

export default function SearchTerms() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { connected, loading: checking } = useAmazonStatus(token);

  const [period, setPeriod] = useState('WEEK');
  const [settings, setSettings] = useState(null);
  const [brandInput, setBrandInput] = useState('');
  const [rivalInput, setRivalInput] = useState('');
  const [openTerm, setOpenTerm] = useState(null);
  const [locked, setLocked] = useState(null);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(true);
    else setError(e.message);
  }, []);

  const load = useCallback(async () => {
    if (!token || !connected) return;
    setError(null);
    try {
      const config = await getSettings(token);
      setSettings(config.settings || config);
      setLocked(config.locked ? config : null);
    } catch (e) { fail(e); }
  }, [token, connected, fail]);

  useEffect(() => { load(); }, [load]);

  const persistSettings = async (next) => {
    try {
      const r = await saveSettings(token, next);
      setSettings(r.settings);
      load();
    } catch (e) { fail(e); }
  };

  const addBrand = (value, key) => {
    const name = value.trim();
    if (!name || !settings) return;
    const list = settings[key] || [];
    if (list.some((b) => b.toLowerCase() === name.toLowerCase())) return;
    setSettings({ ...settings, [key]: [...list, name] });
  };

  function body() {
    if (checking) return <div className={s.empty}>Checking connection…</div>;
    if (!connected || notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon</h2>
          <p className={s.lockedBody}>Search term data comes from your Amazon account.</p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Connect Amazon
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        {/* Collection is refused but stored data exists: say so without hiding
            what was collected earlier behind it. */}
        {locked && (
          <div className={s.coverageWarn}>
            <FiLock /> Amazon is refusing new search term data for this account. Amazon serves
            this report to the owner of a brand, so an authorised seller of someone
            else&apos;s brand is refused even when the account appears in Brand Registry.
          </div>
        )}

        <CollectPanel token={token} period={period} onPeriodChange={setPeriod}
                      onCollected={load} onError={fail} />

        {/* Brands and competitors. Nothing else in Seller Intelligence can set
            these, and the branded split and every competitor finding rest on
            them — a competitor is only ever a name the seller gave us. */}
        <section className={s.panel}>
          <div className={s.sectionTitle}>Brands and competitors</div>
          <p className={s.hint} style={{ margin: '4px 0 12px' }}>
            Your own brands decide which terms count as branded traffic. Competitor brands
            decide whose wins are reported — nothing is inferred, so a rival you do not name
            here is never counted as one.
          </p>

          {!settings ? <div className={s.empty}>Loading…</div> : (
            <>
              {[['brands', 'Your brands', s.chip, brandInput, setBrandInput],
                ['competitors', 'Competitor brands', s.chipRival, rivalInput, setRivalInput]
              ].map(([key, label, chipClass, value, setValue]) => (
                <div key={key} className={s.section} style={{ marginTop: 10 }}>
                  <div className={s.sectionTitle}>{label}</div>
                  <div className={s.chipRow}>
                    {(settings[key] || []).length === 0 && <span className={s.muted}>None</span>}
                    {(settings[key] || []).map((b) => (
                      <span key={b} className={chipClass}>
                        {b}
                        <button className={s.chipRemove} onClick={() => setSettings({
                          ...settings, [key]: settings[key].filter((x) => x !== b) })}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className={s.controls} style={{ marginTop: 8 }}>
                    <input className={s.input} value={value}
                           placeholder={`Add to ${label.toLowerCase()}…`}
                           onChange={(e) => setValue(e.target.value)}
                           onKeyDown={(e) => {
                             if (e.key === 'Enter') { addBrand(value, key); setValue(''); }
                           }} />
                    <button className={s.secondary}
                            onClick={() => { addBrand(value, key); setValue(''); }}>Add</button>
                  </div>
                  {key === 'brands' && !!settings.catalogue_brands?.length && (
                    <div className={s.chipRow} style={{ marginTop: 8 }}>
                      <span className={s.label}>From catalogue:</span>
                      {settings.catalogue_brands.map((b) => (
                        <button key={b} className={s.suggestion}
                                onClick={() => addBrand(b, 'brands')}>+ {b}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button className={s.button} style={{ marginTop: 12 }}
                      onClick={() => persistSettings({ brands: settings.brands,
                                                       competitors: settings.competitors })}>
                Save brand settings
              </button>
            </>
          )}
        </section>

        {/* The analysis moved. Saying where beats leaving someone to hunt for
            a tab that used to be here. */}
        <section className={s.panel}>
          <div className={s.sectionTitle}>The analysis moved</div>
          <p className={s.hint} style={{ margin: '4px 0 12px' }}>
            Each question now has its own screen, so each can be checked against the document
            it answers to. This page keeps what the others cannot do: fetch the reports, and
            say which brands are yours.
          </p>
          <ul className={s.movedList}>
            {MOVED.map(([what, where, to]) => (
              <li key={to}>
                <span className={s.movedWhat}>{what}</span>
                <button className={s.link} onClick={() => navigate(to)}>{where} →</button>
              </li>
            ))}
          </ul>
        </section>

        {openTerm && (
          <TermDetail token={token} term={openTerm} period={period}
                      onClose={() => setOpenTerm(null)} onChanged={load} />
        )}
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Search data</h1></div>
          <p>Fetch the Amazon reports every search feature reads, and say which brands are yours</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
